export type SpecimenIndexMap = Array<number | undefined>;

let specimenUiKeySequence = 0;

export function createSpecimenPopulationSnapshot(specimens: unknown[]): string {
  return JSON.stringify(specimens, (key, value) =>
    key === "uiKey" ? undefined : value
  );
}

export function areSpecimensUnchangedSincePopulation(
  specimens: unknown[],
  snapshot: string
): boolean {
  return createSpecimenPopulationSnapshot(specimens) === snapshot;
}

export function advanceSpecimenPopulationSnapshot(
  snapshot: string,
  currentSpecimens: unknown[],
  nextSpecimens: unknown[]
): string {
  return areSpecimensUnchangedSincePopulation(currentSpecimens, snapshot)
    ? createSpecimenPopulationSnapshot(nextSpecimens)
    : snapshot;
}

export function createSpecimenUiKey(): string {
  specimenUiKeySequence += 1;
  return `specimen-${Date.now()}-${specimenUiKeySequence}`;
}

export function assignSpecimenUiKeys<T extends { uiKey?: string; id?: string }>(
  specimens: T[],
  previousSpecimens: T[] = [],
  oldIndexByNewIndex?: SpecimenIndexMap
): Array<T & { uiKey: string }> {
  return specimens.map((specimen, newIndex) => {
    const oldIndex = oldIndexByNewIndex?.[newIndex];
    const previousKey =
      oldIndex === undefined ? undefined : previousSpecimens[oldIndex]?.uiKey;
    return {
      ...specimen,
      uiKey:
        specimen.uiKey ||
        previousKey ||
        (specimen.id ? `specimen-db-${specimen.id}` : createSpecimenUiKey()),
    };
  });
}

export function reindexSpecimenRecord<T>(
  record: Record<number | string, T>,
  oldIndexByNewIndex: SpecimenIndexMap
): Record<number, T> {
  return oldIndexByNewIndex.reduce<Record<number, T>>(
    (result, oldIndex, newIndex) => {
      if (
        oldIndex !== undefined &&
        Object.prototype.hasOwnProperty.call(record, oldIndex)
      ) {
        result[newIndex] = record[oldIndex];
      }
      return result;
    },
    {}
  );
}

function createNewIndexByOldIndex(
  oldIndexByNewIndex: SpecimenIndexMap
): Map<number, number> {
  const result = new Map<number, number>();
  oldIndexByNewIndex.forEach((oldIndex, newIndex) => {
    if (oldIndex !== undefined) result.set(oldIndex, newIndex);
  });
  return result;
}

export function reindexSpecimenFieldRecord<T>(
  record: Record<string, T>,
  oldIndexByNewIndex: SpecimenIndexMap
): Record<string, T> {
  const newIndexByOldIndex = createNewIndexByOldIndex(oldIndexByNewIndex);
  return Object.entries(record).reduce<Record<string, T>>(
    (result, [field, value]) => {
      const match = field.match(/^case\.specimens\.(\d+)(\..+)$/);
      if (!match) {
        result[field] = value;
        return result;
      }
      const newIndex = newIndexByOldIndex.get(Number(match[1]));
      if (newIndex !== undefined) {
        result[`case.specimens.${newIndex}${match[2]}`] = value;
      }
      return result;
    },
    {}
  );
}
